/**
 * The composer's Preferences chip: how the agent works in this conversation
 * (e.g. ask before guessing, or highlight gaps instead). Shown once anything
 * is set, by the attorney, Casey (`remember_preference`) or the playbook. A
 * pick here applies to this conversation; Clear forgets the value where it
 * was set (the firm's default is an admin's to clear).
 */
import { SlidersHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import {
  AgentAPI,
  type PreferenceScope,
  type ThreadPreference
} from "../api/agentApi";
import type { AgentScope } from "../types";

type Props = {
  scope: AgentScope;
  threadId: string | null;
  /** Changes when the preferences may have moved (a turn ended, the thread changed). */
  refreshKey?: unknown;
  disabled?: boolean;
};

const WHERE: Record<PreferenceScope, string> = {
  thread: "This conversation",
  case: "This case",
  firm: "Firm default"
};

/** "Firm default, from the playbook" / "This case, set by Casey". */
export const preferenceOrigin = (row: ThreadPreference): string => {
  if (!row.scope) return "Default";
  const where = WHERE[row.scope];
  if (row.source?.startsWith("playbook:")) return `${where}, from the playbook`;
  if (row.source === "casey") return `${where}, set by Casey`;
  return where;
};

const errorText = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: { error?: unknown } } })?.response
    ?.data;
  return typeof data?.error === "string" ? data.error : fallback;
};

export const PreferencesChip: FC<Props> = ({
  scope,
  threadId,
  refreshKey,
  disabled
}) => {
  const [rows, setRows] = useState<ThreadPreference[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!threadId) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    AgentAPI.getPreferences(scope, threadId)
      .then((next) => {
        if (!cancelled) setRows(next);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
    // `scope` is an object the host rebuilds; its ids are what matter.
  }, [scope.kind, scope.id, threadId, refreshKey]);

  const pick = useCallback(
    async (key: string, value: string) => {
      if (!threadId) return;
      setSaving(true);
      try {
        await AgentAPI.updateThread(scope, threadId, {
          preferences: { [key]: value }
        });
        setRows(await AgentAPI.getPreferences(scope, threadId));
      } catch (e) {
        toast.error(errorText(e, "Couldn't save the preference"));
      } finally {
        setSaving(false);
      }
    },
    [scope, threadId]
  );

  const clear = useCallback(
    async (row: ThreadPreference) => {
      if (!threadId || !row.scope) return;
      setSaving(true);
      try {
        setRows(
          await AgentAPI.forgetPreference(scope, threadId, row.key, row.scope)
        );
      } catch (e) {
        toast.error(errorText(e, "Couldn't clear the preference"));
      } finally {
        setSaving(false);
      }
    },
    [scope, threadId]
  );

  const set = rows.filter((r) => r.scope);
  if (!threadId || !set.length) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          disabled={disabled}
          className="h-6 gap-1 rounded-full px-2 font-normal text-fg-3 hover:bg-fill-1 aria-expanded:bg-fill-2"
          aria-label={`Preferences (${set.length} set)`}
          data-testid="preferences-chip"
        >
          <SlidersHorizontal className="size-3" />
          Preferences
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0"
        data-testid="preferences-popover"
      >
        <div className="divide-y divide-line-1">
          {rows.map((row) => (
            <div
              key={row.key}
              className="space-y-1.5 px-3 py-2.5"
              data-testid={`preference-${row.key}`}
            >
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
                  {row.label}
                </span>
                {row.scope ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-2xs"
                    disabled={saving}
                    aria-label={`Clear ${row.label}`}
                    data-testid={`preference-clear-${row.key}`}
                    onClick={() => void clear(row)}
                  >
                    <X className="size-3" />
                  </Button>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1">
                {row.options.map((o) => (
                  <Button
                    key={o.value}
                    type="button"
                    size="xs"
                    variant={o.value === row.value ? "tonal" : "ghost"}
                    disabled={saving}
                    aria-pressed={o.value === row.value}
                    className={cn(o.value !== row.value && "text-fg-3")}
                    data-testid={`preference-${row.key}-${o.value}`}
                    onClick={() => {
                      if (o.value !== row.value) void pick(row.key, o.value);
                    }}
                  >
                    {o.label}
                  </Button>
                ))}
              </div>
              <p className="text-sm text-fg-3">{preferenceOrigin(row)}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
